/**
 * Environment configuration module
 * Centralizes all environment variable access with type safety and defaults
 */

interface EnvironmentConfig {
  // API Configuration
  apiUrl: string;
  apiTimeout: number;

  // Application Settings
  appName: string;
  appVersion: string;
  isDevelopment: boolean;
  isProduction: boolean;
  debugMode: boolean;

  // Third-party Services
  geminiApiKey?: string;
  appInsightsKey?: string;

  // Feature Flags
  enableAnalytics: boolean;
  enableDebugMode: boolean;
  enableSocketAuth: boolean;
  enableAutoUpdate: boolean;
}

/**
 * Safely get environment variable with fallback
 */
function getEnvVar(key: string, defaultValue?: string): string | undefined {
  // Check if we're in a browser environment
  if (typeof import.meta !== "undefined" && import.meta.env) {
    return import.meta.env[key] || defaultValue;
  }

  // Fallback for Node.js environment (testing, SSR, etc.)
  if (typeof process !== "undefined" && process.env) {
    return process.env[key] || defaultValue;
  }

  return defaultValue;
}

/**
 * Parse boolean environment variables
 */
function parseBoolean(
  value: string | undefined,
  defaultValue: boolean
): boolean {
  if (value === undefined) return defaultValue;
  return value.toLowerCase() === "true";
}

/**
 * Parse number environment variables
 */
function parseNumber(value: string | undefined, defaultValue: number): number {
  if (value === undefined) return defaultValue;
  const parsed = parseInt(value, 10);
  return isNaN(parsed) ? defaultValue : parsed;
}

/**
 * Get API URL based on environment
 */
function getApiUrl(): string {
  const envUrl = getEnvVar("VITE_API_URL");
  if (envUrl) return envUrl;

  // Default URLs based on environment
  const mode = getEnvVar("MODE") || "development";
  switch (mode) {
    case "production":
      return "https://miller-mize-api.intrasync.app";
    case "staging":
      return "https://stage.miller-mize-api.intrasync.app";
    default:
      return "http://localhost:3355";
  }
}

/**
 * Main environment configuration object
 * All environment variables should be accessed through this object
 */
export const env: EnvironmentConfig = {
  // API Configuration
  apiUrl: getApiUrl(),
  apiTimeout: parseNumber(getEnvVar("VITE_API_TIMEOUT"), 30000),

  // Application Settings
  appName: getEnvVar("VITE_APP_NAME", "intrasyncdemo")!,
  appVersion: getEnvVar("VITE_APP_VERSION", "0.0.1")!,
  isDevelopment: getEnvVar("MODE") === "development",
  isProduction: getEnvVar("MODE") === "production",
  debugMode: parseBoolean(getEnvVar("VITE_DEBUG_MODE"), false),

  // Third-party Services (optional)
  geminiApiKey: getEnvVar("VITE_GEMINI_API_KEY"),
  appInsightsKey: getEnvVar("VITE_APP_INSIGHTS_KEY"),

  // Feature Flags
  enableAnalytics: parseBoolean(getEnvVar("VITE_ENABLE_ANALYTICS"), true),
  enableDebugMode: parseBoolean(getEnvVar("VITE_ENABLE_DEBUG_MODE"), false),
  enableSocketAuth: parseBoolean(getEnvVar("VITE_ENABLE_SOCKET_AUTH"), true),
  enableAutoUpdate: parseBoolean(getEnvVar("VITE_ENABLE_AUTO_UPDATE"), true),
};

/**
 * Validate required environment variables
 * Call this during app initialization
 */
export function validateEnvironment(): void {
  const errors: string[] = [];

  // Add validation for required variables
  if (!env.apiUrl) {
    errors.push("API URL is not configured");
  }

  // In production, ensure certain security features are enabled
  if (env.isProduction) {
    if (!env.enableSocketAuth) {
      errors.push("Socket authentication must be enabled in production");
    }

    if (env.debugMode) {
      errors.push("Debug mode must be disabled in production");
    }
  }

  // Log warnings for missing optional configurations
  if (!env.appInsightsKey && env.isProduction) {
    console.warn("Application Insights key not configured for production");
  }

  // Throw if critical errors exist
  if (errors.length > 0) {
    throw new Error(`Environment validation failed:\n${errors.join("\n")}`);
  }
}

/**
 * Logger that respects debug mode setting
 */
export const logger = {
  log: (...args: unknown[]) => {
    if (env.enableDebugMode || env.isDevelopment) {
      console.log(...args);
    }
  },

  error: (...args: unknown[]) => {
    console.error(...args);
  },

  warn: (...args: unknown[]) => {
    if (env.enableDebugMode || env.isDevelopment) {
      console.warn(...args);
    }
  },

  info: (...args: unknown[]) => {
    if (env.enableDebugMode || env.isDevelopment) {
      console.info(...args);
    }
  },

  debug: (...args: unknown[]) => {
    if (env.enableDebugMode) {
      console.debug(...args);
    }
  },
};

// Freeze the configuration to prevent runtime modifications
Object.freeze(env);
